import axios from "axios";
import { 
    API_ENDPOINTS,
    customToast,
    DataTable,
    DateFormats,
    Icon,
    Icons,
    PageLayout, 
    Permissions, 
    route, 
    StringHelpers, 
    useAuthorisationContext, 
    useLangContext, 
    Variants
} from "carrier-fe";
import { formatDate, subDays } from "date-fns";
import { useMemo } from "react";

interface TrainingReportData {
    id: string;
    type: string;
    attendee_id: string;
    attendee_name: string;
    attendee_phone: string;
    attendee_email: string;
    company_name: string;
    name: string;
    event_at: string;
    attended: string;
    passed: string;
    requires_payment: string;
    display_paid_price: string;
}

function TrainingReportList() {
    const { crud, fields, responses } = useLangContext();
    const { hasPermissionAccess } = useAuthorisationContext();

    const convertBinary = (oneOrZero: string) => {
        switch (oneOrZero) {
            case "1": return StringHelpers.title(fields?.yes || 'Yes');
            case "0": return StringHelpers.title(fields?.no || 'No');
            default: return oneOrZero || '-';
        }
    };

    const exportReport = async () => {
        const searchParams = new URLSearchParams(window.location.search);
		const startDate = searchParams.get('filter[from_date]');
		const endDate = searchParams.get('filter[to_date]');
        const search = searchParams.get('filter[search]');
        const type = searchParams.get('filter[type]');

		try {
			await axios.get(
				route(
					API_ENDPOINTS.ADMIN.TRAINING.REPORT.EXPORT
				),
				{
					params: {
						'filter[from_date]': startDate,
						'filter[to_date]': endDate,
                        'filter[search]': search,
                        'filter[type]': type
					},
				}
			)
			customToast({
				title:
					responses?.admin.reporting.export.success ||
					'Success. Report downloaded successfully.',
				variant: Variants.Success,
			});
		} catch (error: any) {
			customToast({
				title: error?.response?.data?.message || '',
				variant: Variants.Danger,
			});
		}
	};

    const items = useMemo(() => {
		if (!hasPermissionAccess(Permissions.ADMIN_INDIVIDUAL_TRAININGS_INDEX)) return [];

		return [
			{
				label: StringHelpers.title(
					crud?.buttons?.export?.default || 'Export'
				),
				disabled: false,
				onClick: () => exportReport(),
				icon: <Icon icon={Icons.ADD} />,
			},
		];
	}, []);

    const colMapping = (data: TrainingReportData) => [
        {
            id: 'attendee_name',
            label: StringHelpers.title(fields?.contact_name || 'Contact Name'),
            value: data?.attendee_name || '-',
            sortable: true,
        },
        {
            id: 'attendee_phone',
            label: StringHelpers.title(fields?.contact_tel_number || 'Contact Tel Number'),
            value: data?.attendee_phone || '-',
            sortable: true,
        },
        {
            id: 'attendee_email',
            label: StringHelpers.title(fields?.email || 'Email'),
            value: data?.attendee_email || '-',
            sortable: true,
        },
        {
            id: 'company_name',
            label: StringHelpers.title(fields?.company_name || 'Company Name'),
            value: data?.company_name || '-',
            sortable: true,
        },
        {
            id: 'name',
            label: StringHelpers.title(fields?.course_name || 'Course Name'),
            value: data?.name || '-',
            sortable: true,
        },
        {
            id: 'event_at',
            label: StringHelpers.title(fields?.event_at || 'Date'),
            value: formatDate(data?.event_at, DateFormats.DATE) || '-',
            sortable: true,
        },
        {
            id: 'attended',
            label: StringHelpers.title(fields?.attended || 'Attended'),
            value: convertBinary(data?.attended),
            sortable: true,
        },
        {
            id: 'requires_payment',
            label: StringHelpers.title(fields?.requires_payment || 'Requires Payment'),
            value: convertBinary(data?.requires_payment),
            sortable: true,
        },
        {
            id: 'display_paid_price',
            label: StringHelpers.title(fields?.paid || 'Paid'),
            value: data?.display_paid_price || '-',
            sortable: true,
        },
    ];

    const title = useMemo(
		() =>
			StringHelpers.title(
				crud?.models.training_report || 'Training Report'
			),
		[crud]
	);

    return (
        <PageLayout title={title}>
            <DataTable<TrainingReportData>
                routeEndpoint={route(
                    API_ENDPOINTS.ADMIN.TRAINING.REPORT.INDEX
                )}
                colMapping={colMapping}
                incrementPaginationButtons
                items={items}
                filters={[
					{
						name: 'from_date',
						label: StringHelpers.title(
							fields?.start_date || 'Start Date'
						),
						type: 'date',
						value: formatDate(
							subDays(new Date(), 30).setHours(0, 0, 0, 0),
							DateFormats.DB
						),
					},
					{
						name: 'to_date',
						label: StringHelpers.title(
							fields?.end_date || 'End Date'
						),
						type: 'date',
						value: formatDate(
							new Date().setHours(0, 0, 0, 0),
							DateFormats.DB
						),
					},
                    {
                        name: 'type',
                        label: StringHelpers.title(
                            fields?.type || 'Type'
                        ),
                        type: 'searchable-select',
                        placeholder: StringHelpers.title(fields?.please_select || 'Please Select'),
                        options: crud?.options?.type,
                        clearable: false,
                    }
				]}
            />
        </PageLayout>
    );
};

export default TrainingReportList;